import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IDiscountedFood } from "../models/IDiscountedFood";
import { getDiscountedFoods } from "../services/restaurants";
import { S3_IMAGE_BASE_URL } from "../services/config";
import { getDefaultAddress, isLoggedIn } from "../utils/tokenUtils";
import { useLanguage } from "../context/LanguageContext";
import "../styles/style.css";

const PAGE_SIZE = 30;
// İlk açılışta gösterilen restoran sayısı; "Daha fazla" ile bu kadar artar.
const GROUP_STEP = 3;

interface IDiscountGroup {
  restaurant: IDiscountedFood["restaurant"];
  items: IDiscountedFood[];
}

/**
 * Sunucu sırasını (önce açık restoranlar, sonra en büyük indirim) bozmadan restorana göre
 * gruplar: grup, restoranın ilk ürününün geldiği yerde durur.
 */
const groupByRestaurant = (items: IDiscountedFood[]): IDiscountGroup[] => {
  const groups = new Map<string, IDiscountGroup>();
  for (const item of items) {
    const group = groups.get(item.restaurant.id);
    if (group) group.items.push(item);
    else groups.set(item.restaurant.id, { restaurant: item.restaurant, items: [item] });
  }
  return Array.from(groups.values());
};

/** "%25'e", "%20'ye", "%30'a" — yüzdenin okunuşuna göre yönelme eki. */
const turkishDativeSuffix = (n: number) => {
  const ones = ["", "'e", "'ye", "'e", "'e", "'e", "'ya", "'ye", "'e", "'a"];
  const tens = ["", "'a", "'ye", "'a", "'a", "'ye", "'a", "'e", "'e", "'a"];
  if (n % 100 === 0) return "'e"; // yüz
  if (n % 10 !== 0) return ones[n % 10];
  return tens[(n % 100) / 10];
};

/** 240 → "240 TL", 900.9 → "900,90 TL" (aramadaki gibi fiyat ve simge arasında boşluk). */
const formatPrice = (value: number, currency: string, lang: string) => {
  const digits = Number.isInteger(value) ? 0 : 2;
  const amount = value.toLocaleString(lang === "en" ? "en-GB" : "tr-TR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return `${amount} ${currency}`.trim();
};

/**
 * Ana sayfadaki "Ürünlerde %X'e varan indirim" bölümü. Aramadaki gibi restoran başına bir
 * başlık ve altında kampanyalı yemeklerinin yatay kayan şeridi. Kampanya yoksa hiç görünmez.
 */
function DiscountedFoods() {
  const { t } = useLanguage();
  const [items, setItems] = useState<IDiscountedFood[]>([]);
  const [maxPercent, setMaxPercent] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [visibleGroups, setVisibleGroups] = useState(GROUP_STEP);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const townId = isLoggedIn()
    ? getDefaultAddress()?.townId
    : localStorage.getItem("guestTownId") ?? undefined;

  useEffect(() => {
    getDiscountedFoods(townId, 0, PAGE_SIZE)
      .then((res) => {
        setItems(res.data.items);
        setMaxPercent(res.data.maxDiscountPercent);
        setHasNextPage(res.data.paging.hasNextPage);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groups = groupByRestaurant(items);

  const showMore = async () => {
    // Elde gösterilmemiş restoran yoksa bir sonraki sayfayı çek
    if (visibleGroups >= groups.length && hasNextPage) {
      setLoadingMore(true);
      try {
        const next = pageIndex + 1;
        const res = await getDiscountedFoods(townId, next, PAGE_SIZE);
        setItems((prev) => [...prev, ...res.data.items]);
        setHasNextPage(res.data.paging.hasNextPage);
        setPageIndex(next);
      } catch {
        setHasNextPage(false);
      } finally {
        setLoadingMore(false);
      }
    }
    setVisibleGroups((v) => v + GROUP_STEP);
  };

  if (loading) {
    return (
      <section className="deals">
        <div className="skeleton deals__skeleton-hero" />
        <div className="deals__track">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="skeleton deals__skeleton-card" />
          ))}
        </div>
      </section>
    );
  }

  if (groups.length === 0) return null;

  const percent = Math.floor(maxPercent);
  const title =
    percent > 0
      ? t("discounts.title", { percent, suffix: turkishDativeSuffix(percent) })
      : t("discounts.titleNoPercent");

  return (
    <section className="deals">
      <div className="deals__hero">
        <span className="deals__hero-icon">
          <i className="bi bi-percent" />
        </span>
        <div className="deals__hero-text">
          <h6 className="deals__hero-title">{title}</h6>
          <div className="deals__hero-sub">{t("discounts.subtitle")}</div>
        </div>
      </div>

      <div className="deals__groups">
        {groups.slice(0, visibleGroups).map((group) => (
          <DiscountGroup key={group.restaurant.id} group={group} />
        ))}
      </div>

      {(visibleGroups < groups.length || hasNextPage) && (
        <button type="button" className="deals__more" onClick={showMore} disabled={loadingMore}>
          {loadingMore ? (
            <span className="spinner-border spinner-border-sm" />
          ) : (
            <>
              {t("discounts.showMore")} <i className="bi bi-chevron-down ms-1" />
            </>
          )}
        </button>
      )}
    </section>
  );
}

function DiscountGroup({ group }: { group: IDiscountGroup }) {
  const { restaurant, items } = group;
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const trackRef = useRef<HTMLDivElement>(null);

  const openRestaurant = () => navigate(`/restaurants/${restaurant.id}`);

  const scrollBy = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  };

  return (
    <div className={`deals-group${restaurant.isOpenNow ? "" : " deals-group--closed"}`}>
      <button type="button" className="deals-group__header" onClick={openRestaurant}>
        <span className="deals-group__logo">
          {restaurant.logoPath ? (
            <img src={S3_IMAGE_BASE_URL + restaurant.logoPath} alt="" />
          ) : (
            <i className="bi bi-shop" />
          )}
        </span>
        <span className="deals-group__info">
          <span className="deals-group__name">{restaurant.name}</span>
          <span className="deals-group__meta">
            {restaurant.isOpenNow ? (
              t("discounts.itemCount", { count: items.length })
            ) : (
              <span className="deals-group__closed-badge">
                <i className="bi bi-moon-fill me-1" />
                {t("restaurants.closed")}
              </span>
            )}
          </span>
        </span>
        <i className="bi bi-chevron-right deals-group__chevron" />
      </button>

      <div className="deals-group__rail">
        <button
          type="button"
          className="deals-group__arrow deals-group__arrow--prev"
          onClick={() => scrollBy(-1)}
          aria-label={t("categories.scrollPrev")}
        >
          <i className="bi bi-chevron-left" />
        </button>

        <div className="deals__track" ref={trackRef}>
          {items.map((item) => {
            const name = (lang === "en" && item.food.foodNameEn) || item.food.foodNameTr;
            const percent = Math.floor(item.discountPercent);
            return (
              <button key={item.food.id} type="button" className="deal-card" onClick={openRestaurant}>
                <span className="deal-card__media">
                  {item.food.foodImagePath ? (
                    <img src={S3_IMAGE_BASE_URL + item.food.foodImagePath} alt={name} loading="lazy" />
                  ) : (
                    <span className="deal-card__placeholder">
                      <i className="bi bi-egg-fried" />
                    </span>
                  )}
                  {percent > 0 && (
                    <span className="deal-card__badge">{t("discounts.badge", { percent })}</span>
                  )}
                </span>
                <span className="deal-card__body">
                  <span className="deal-card__name">{name}</span>
                  <span className="deal-card__prices">
                    <span className="deal-card__price">
                      {formatPrice(item.discountedPrice, item.currencyDisplayText, lang)}
                    </span>
                    <s className="deal-card__list-price">
                      {formatPrice(item.listPrice, item.currencyDisplayText, lang)}
                    </s>
                  </span>
                  {percent > 0 && (
                    <span className="deal-card__off">
                      <i className="bi bi-tag-fill me-1" />
                      {t("discounts.percentOff", { percent })}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className="deals-group__arrow deals-group__arrow--next"
          onClick={() => scrollBy(1)}
          aria-label={t("categories.scrollNext")}
        >
          <i className="bi bi-chevron-right" />
        </button>
      </div>
    </div>
  );
}

export default DiscountedFoods;
